import { axiosBaseQuery } from '@client';
import { createApi } from '@reduxjs/toolkit/dist/query/react';
import { BaseVesselResponse } from '../../types/BaseVesselResponse';
import { PortEstimatorCalculatorExecutionResultResponse } from '../../types/PortEstimatorCalculatorExecutionResultResponse';
import { PortResponse } from '../../types/PortResponse';
import { RefData } from '../../types/RefData';
import { PortEstimatorParametersResponse } from '../../types/PortEstimatorParametersResponse';

export type PortCostEstimatorLoadConfigArgs = {
  portIdValue: string;
  vesselIdValue: number;
  isModelVessel: boolean;
  activities?: string[];
  terminalId?: number;
  berthId?: number;
};

const buildLoadConfigurationQuery = ({
  portIdValue,
  vesselIdValue,
  isModelVessel,
  activities,
  terminalId,
  berthId
}: PortCostEstimatorLoadConfigArgs): string => {
  const params = new URLSearchParams();
  params.set('isModelVessel', String(isModelVessel));

  activities?.forEach((activity) => {
    params.append('activities', activity);
  });

  if (terminalId != null) {
    params.set('terminalId', String(terminalId));
  }

  if (berthId != null) {
    params.set('berthId', String(berthId));
  }

  return `/port/${portIdValue}/vessel/${vesselIdValue}/load-configuration?${params.toString()}`;
};

export const portCostEstimatorApi = createApi({
  baseQuery: axiosBaseQuery({
    baseUrl: 'secured/port-estimator-calculator'
  }),
  reducerPath: 'portCostEstimatorApi',
  endpoints: (build) => ({
    portCostEstimatorExecute: build.mutation<
      PortEstimatorCalculatorExecutionResultResponse,
      {
        vesselId: number;
        portId: string;
        activityTypeIds: number[];
        cargoId?: number | null;
        advanced: boolean;
        berthId: number;
        terminalId: number;
        modelVessel: boolean;
      }
    >({
      query: (data) => ({ url: `/execute`, method: 'POST', data })
    }),
    portCostEstimatorFilterVessels: build.query<
      RefData[],
      { text: string; isModelVessel: boolean }
    >({
      query: ({ text, isModelVessel }) => ({
        url: `/filter-vessels?text=${text}&isModelVessel=${isModelVessel}`,
        method: 'GET'
      })
    }),
    portCostEstimatorLoadPort: build.query<PortResponse, { portId: string }>({
      query: ({ portId }) => ({
        url: `/port/${portId}/load`,
        method: 'GET'
      })
    }),
    portCostEstimatorLoadConfig: build.query<
      PortEstimatorParametersResponse,
      PortCostEstimatorLoadConfigArgs
    >({
      query: (args) => ({
        url: buildLoadConfigurationQuery(args),
        method: 'GET'
      })
    }),
    portCostEstimatorLoadVessel: build.query<
      BaseVesselResponse,
      { vesselId: number; isModelVessel: boolean }
    >({
      query: ({ vesselId, isModelVessel }) => ({
        url: `/vessel/${vesselId}/load?isModelVessel=${isModelVessel}`,
        method: 'GET'
      })
    })
  })
});

export const {
  useLazyPortCostEstimatorFilterVesselsQuery,
  useLazyPortCostEstimatorLoadVesselQuery,
  usePortCostEstimatorExecuteMutation,
  usePortCostEstimatorLoadConfigQuery,
  usePortCostEstimatorLoadPortQuery,
  usePortCostEstimatorLoadVesselQuery
} = portCostEstimatorApi;
